/**
 * 订单详情抽屉 - 时间安排区块
 *
 * 展示日程徽章、开始/结束日期、活动天数、服务时间、服务时长与到达时间
 * （含提前/准时/延迟状态徽标），支持内联切换为编辑表单（全天/指定时段切换）。
 * 受控组件，编辑状态与保存回调均由父组件编排。
 *
 * 服务时长不是字段：库里没有 activity_duration 这一列，它由父组件用表单弹窗同一个
 * `calcDuration` 按起止时间实时算出来，这里只读结果。编辑态另给一份（跟着正在改的值走），
 * 所以两态都不收用户输入——曾经这里是个自由文本框，填进去的话当场可见、刷新即失。
 */
import { bookingDays } from '../../../../../utils/dateUtils';
import { fmtDate, calcArrivalStatus } from '../utils';
import InfoRow from '../InfoRow';

interface Props {
  localStartDate: string;
  localEndDate: string;
  localIsFullDay: boolean;
  localTimeStart: string;

  durationHint: string;
  localArrivalDateTime: string;
  displayTimeValue: string;
  scheduleBadge: { label: string; style: string; icon: string; pulse: string } | null;
  showScheduleEdit: boolean;
  /** 后端投影 canEditOrder：非建单方与终态单只读，不给内联编辑入口 */
  canEdit: boolean;
  editStartDate: string;
  editEndDate: string;
  editIsFullDay: boolean;
  editTimeStart: string;
  editTimeEnd: string;
  editDurationHint: string;
  editArrivalDateTime: string;
  scheduleError: string;
  scheduleSaving: boolean;
  onOpenEdit: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onEditEndDateChange: (value: string) => void;
  onEditIsFullDayChange: (value: boolean) => void;
  onEditTimeStartChange: (value: string) => void;
  onEditTimeEndChange: (value: string) => void;
  onEditArrivalDateTimeChange: (value: string) => void;
}

export default function ScheduleSection({
  localStartDate,
  localEndDate,
  localIsFullDay,
  localTimeStart,

  durationHint,
  localArrivalDateTime,
  displayTimeValue,
  scheduleBadge,
  showScheduleEdit,
  canEdit,
  editStartDate,
  editEndDate,
  editIsFullDay,
  editTimeStart,
  editTimeEnd,
  editDurationHint,
  editArrivalDateTime,
  scheduleError,
  scheduleSaving,
  onOpenEdit,
  onCancelEdit,
  onSave,
  onEditEndDateChange,
  onEditIsFullDayChange,
  onEditTimeStartChange,
  onEditTimeEndChange,
  onEditArrivalDateTimeChange,
}: Props) {
  return (
    <>
      {/* Schedule */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">时间安排</p>
          {!showScheduleEdit && canEdit && (
            <button onClick={onOpenEdit} className="flex items-center gap-1 text-xs text-teal-500 hover:text-teal-600 cursor-pointer transition-colors whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-edit-2-line text-sm" /></div>
              编辑
            </button>
          )}
        </div>
        {showScheduleEdit ? (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 space-y-3">
            <p className="text-xs font-medium text-teal-700 flex items-center gap-1.5"><i className="ri-calendar-2-line text-sm" />修改时间安排</p>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">开始日期 <span className="text-rose-400">*</span></label>
              {/* 预约日期提交后不可更改（服务端同一道闸门）：这一格只显示，改期请取消这张单后重新提交 */}
              <div className="w-full flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 border border-gray-200 rounded-lg text-gray-500">
                <span>{editStartDate || '—'}</span>
                <i className="ri-lock-line text-gray-400 text-xs ml-auto" />
              </div>
              <p className="text-[11px] text-gray-400 mt-1">提交后不可更改；如需改期请取消这张单后重新提交</p>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">结束日期（多天活动填写）</label>
              <input type="date" value={editEndDate} onChange={e => onEditEndDateChange(e.target.value)} min={editStartDate} className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all text-gray-700" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-2 block">服务时间</label>
              <div className="flex gap-2 mb-2">
                <button type="button" onClick={() => onEditIsFullDayChange(true)} className={`flex-1 py-2 text-xs rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${editIsFullDay ? 'bg-teal-500 text-white border-teal-500' : 'bg-white text-gray-500 border-gray-200 hover:border-teal-300'}`}>全天</button>
                <button type="button" onClick={() => onEditIsFullDayChange(false)} className={`flex-1 py-2 text-xs rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${!editIsFullDay ? 'bg-teal-500 text-white border-teal-500' : 'bg-white text-gray-500 border-gray-200 hover:border-teal-300'}`}>指定时段</button>
              </div>
              {!editIsFullDay && (
                <div className="flex items-center gap-2">
                  <input type="time" value={editTimeStart} onChange={e => onEditTimeStartChange(e.target.value)} className="flex-1 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all text-gray-700" />
                  <span className="text-xs text-gray-400 shrink-0">至</span>
                  <input type="time" value={editTimeEnd} onChange={e => onEditTimeEndChange(e.target.value)} className="flex-1 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all text-gray-700" />
                </div>
              )}
            </div>
            {editDurationHint && (
              <div className="flex items-center gap-2.5 p-3 bg-white border border-teal-100 rounded-lg">
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-time-line text-teal-500 text-sm" />
                </div>
                <span className="text-xs text-gray-500">服务时长</span>
                <span className="text-sm font-semibold text-teal-600">{editDurationHint}</span>
              </div>
            )}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">预计到达时间（选填）</label>
              <input type="datetime-local" value={editArrivalDateTime} onChange={e => onEditArrivalDateTimeChange(e.target.value)} className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 transition-all text-gray-700" />
            </div>
            {scheduleError && <p className="text-xs text-rose-500 flex items-center gap-1"><i className="ri-error-warning-line text-sm" />{scheduleError}</p>}
            <div className="flex gap-2 pt-1">
              <button onClick={onCancelEdit} disabled={scheduleSaving} className="flex-1 py-2 text-sm border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors bg-white">取消</button>
              <button onClick={onSave} disabled={scheduleSaving} className="flex-1 py-2 text-sm bg-teal-500 text-white rounded-lg hover:bg-teal-600 cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5">
                {scheduleSaving ? <><i className="ri-loader-4-line animate-spin text-sm" />保存中...</> : <><i className="ri-check-line text-sm" />确认保存</>}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-xl p-4">
            {scheduleBadge && (
              <div className="flex items-center gap-1.5 mb-3 pb-3 border-b border-gray-100">
                <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold ${scheduleBadge.style}`}>
                  <div className="w-3.5 h-3.5 flex items-center justify-center"><i className={`${scheduleBadge.icon} text-xs`} /></div>
                  {scheduleBadge.label}
                </div>
                {scheduleBadge.pulse && <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${scheduleBadge.pulse}`} />}
              </div>
            )}
            <InfoRow icon="ri-calendar-line" label="开始日期" value={fmtDate(localStartDate)} />
            {localEndDate && localEndDate !== localStartDate && <InfoRow icon="ri-calendar-check-line" label="结束日期" value={fmtDate(localEndDate)} />}
            {localEndDate && localEndDate !== localStartDate && (() => {
              const days = bookingDays(localStartDate, localEndDate);
              return (
                <InfoRow
                  icon="ri-moon-line"
                  label="活动天数"
                  value={days === null ? '-' : `${days} 天`}
                />
              );
            })()}
            <InfoRow icon="ri-time-line" label="服务时间" value={displayTimeValue} />
            {durationHint && <InfoRow icon="ri-timer-line" label="服务时长" value={durationHint} />}
            {localArrivalDateTime && (() => {
              const arrivalStatus = calcArrivalStatus(
                localArrivalDateTime,
                localStartDate,
                localTimeStart,
                localIsFullDay
              );
              const arrDisplay = new Date(localArrivalDateTime).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
              return (
                <div className="flex items-start gap-2.5 py-2 border-b border-gray-50 last:border-0">
                  <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-login-box-line text-sm text-gray-400" />
                  </div>
                  <span className="text-xs text-gray-400 w-16 shrink-0 pt-0.5">到达时间</span>
                  <div className="flex-1 flex flex-col gap-1">
                    <span className="text-sm text-gray-700">{arrDisplay}</span>
                    {arrivalStatus && (
                      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border w-fit font-medium ${arrivalStatus.style}`}>
                        <i className={`${arrivalStatus.icon} text-[10px]`} />
                        {arrivalStatus.label}
                        {!localIsFullDay && localTimeStart && (
                          <span className="opacity-60 ml-0.5">
                            vs {localTimeStart.slice(0, 5)}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </>
  );
}